import { InView } from 'react-intersection-observer';
import { isEqual } from 'lodash';
import { styled } from 'styled-components';
import DeliveryCard from './components/DeliveryCard';
import FilterContainer from './components/FilterContainer';
import { useDeliveryHooks } from './hooks/useDeliveryHooks';

import { ICON_REFRESH } from 'assets/icons';
import Column from 'components/common/Column';
import CommonImage from 'components/common/CommonImage';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import Loading from 'components/Loading/Loading';
import DeliveryDetail from './components/DeliveryDetail';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import ContentsTopBar from 'components/common/ContentsTopBar';

const Container = styled.div<{ windowHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  width: 100%;
  max-width: 1200px;
  background-color: #fff;
  height: ${props => props.windowHeight}px;

  &::-webkit-scrollbar-thumb {
    background-color: #eaebec;
    border: 1px solid red;
  }
`;

const DeliveryContainer = () => {
  const {
    ref,
    windowHeight,
    isMobile,
    deliveryList,
    deliveryParam,
    selectedDelivery,
    isOpenDeliveryDetail,
    isSearch,
    handleChangeDeliveryParam,
    handleRefetchDeliveryList,
    handleFilterClick,
    handleAccpetSearchKeyword,
    handleSelectDelivery,
    handleCloseDeliveryDetail,
    handleMoreDeliveryList,
    handleReload,
  } = useDeliveryHooks();

  if (!deliveryList) {
    return <Loading />;
  }

  const renderContent = (): JSX.Element => {
    return (
      <Column align="flex-start">
        <ContentsTopBar
          isMobile={isMobile}
          title="배송 관리"
          style={{ top: 0 }}
        />
        <Column align="flex-start">
          <FilterContainer
            isMobile={isMobile}
            deliveryParam={deliveryParam}
            isSearch={isSearch}
            handleFilterClick={handleFilterClick}
            handleAccpetSearchKeyword={handleAccpetSearchKeyword}
          />
          <Column gap="48px">
            {deliveryList.totalCount === 0 ? (
              <EmptySettlement
                textContent="내역이 없어요"
                style={{ minHeight: '0px', paddingTop: '64px' }}
              />
            ) : (
              <Column align="flex-start" gap="8px">
                <Text
                  color="#1b1c1e"
                  size="16px"
                  weight={600}
                  style={{ padding: isMobile ? '0 20px' : '0 40px' }}
                >
                  전체 {deliveryList.totalCount}건
                </Text>
                <Column>
                  {deliveryList.edges.map(item => {
                    return (
                      <DeliveryCard
                        key={item.node.id}
                        isMobile={isMobile}
                        deliveryItem={item.node}
                        isEqual={isEqual(item.node, selectedDelivery)}
                        handleSelectDelivery={handleSelectDelivery}
                      />
                    );
                  })}
                </Column>
              </Column>
            )}
            <Row
              gap="4px"
              style={{
                padding: '8px 12px',
                width: '90px',
                borderRadius: '8px',
                background: '#f4f4f5',
                cursor: 'pointer',
              }}
              onClick={handleReload}
            >
              <CommonImage
                src={ICON_REFRESH}
                onClick={() => {}}
                style={{ width: '14px', pointerEvents: 'none' }}
              />
              <Text color="#46474c" weight={600}>
                새로고침
              </Text>
            </Row>
            <InView
              onChange={inView => {
                if (deliveryList.pageInfo.hasNextPage && inView) {
                  handleMoreDeliveryList();
                }
              }}
            />
          </Column>
        </Column>
      </Column>
    );
  };

  const renderResponsiveContent = (): JSX.Element => {
    if (isMobile) {
      return (
        <>
          {renderContent()}
          {isOpenDeliveryDetail && (
            <DeliveryDetail
              isMobile={isMobile}
              selectedDelivery={selectedDelivery}
              handleCloseDeliveryDetail={handleCloseDeliveryDetail}
              handleRefetchDeliveryList={handleRefetchDeliveryList}
            />
          )}
        </>
      );
    }

    return (
      <Row
        align="flex-start"
        style={{
          width: '100%',
          borderLeft: '1px solid #dbdcdf',
          borderRight: '1px solid #dbdcdf',
        }}
      >
        <Column
          gap="24px"
          align="flex-start"
          justifyContent="flex-start"
          style={{
            paddingBottom: '64px',
            height: `${windowHeight}px`,
            minHeight: `${windowHeight}px`,
            borderRight: '1px solid #dbdcdf',
            overflow: 'auto',
          }}
        >
          {renderContent()}
        </Column>
        <Column
          gap="24px"
          align="flex-start"
          justifyContent="flex-start"
          style={{
            paddingBottom: '64px',
            maxWidth: '410px',
            height: `${windowHeight}px`,
            minHeight: `${windowHeight}px`,
            overflow: 'auto',
          }}
        >
          <DeliveryDetail
            isMobile={isMobile}
            selectedDelivery={selectedDelivery}
            handleCloseDeliveryDetail={handleCloseDeliveryDetail}
            handleRefetchDeliveryList={handleRefetchDeliveryList}
          />
        </Column>
      </Row>
    );
  };

  return (
    <Container
      ref={ref}
      windowHeight={windowHeight}
      style={{ paddingBottom: isMobile ? '64px' : 0 }}
    >
      {renderResponsiveContent()}
    </Container>
  );
};

export default DeliveryContainer;
